import { KPI_SOURCE_LABELS, formatNumber, formatPercent, type KpiDto } from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useFilterParams } from '../app/FilterProvider.js';
import { useChartTheme } from '../app/ThemeProvider.js';
import { areaColorFor } from '../lib/area-color.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { StatCard } from '../components/StatCard.js';
import { api, query } from '../lib/api.js';
import { cn } from '../lib/cn.js';

const MONTHS_SHORT = [
  'jan',
  'feb',
  'mar',
  'apr',
  'mai',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'des',
];

export function KpiPage() {
  const params = useFilterParams();
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: kpis = [], isLoading } = useQuery({
    queryKey: ['kpis', params],
    queryFn: () => api.get<KpiDto[]>(`/kpis${query({ ...params, includeDatapoints: true })}`),
  });

  const active = kpis.find((k) => k.id === selected) ?? kpis[0] ?? null;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-ink text-xl">KPI</h1>
        <p className="text-ink-soft mt-0.5 text-sm">
          Måltallene fra markedsplanen 2026–2027. Nova oppdaterer de interne tallene selv.
        </p>
      </header>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonPanel key={i} lines={2} />
          ))}
        </div>
      ) : kpis.length === 0 ? (
        <GlassPanel>
          <EmptyState title="Ingen KPI-er er definert for dette utvalget." />
        </GlassPanel>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map((kpi) => (
              <StatCard
                key={kpi.id}
                label={kpi.name}
                value={kpi.currentValue ?? 0}
                unit={kpi.unit}
                previous={kpi.previousValue}
                target={kpi.targetValue}
                lowerIsBetter={kpi.unit === 'timer' || kpi.unit === 'dager'}
                accentColor={kpi.area?.color ?? undefined}
                series={kpi.datapoints?.map((d) => ({ value: d.value }))}
                onClick={() => setSelected(kpi.id)}
              />
            ))}
          </div>

          {active && <KpiDetail kpi={active} canRegister={user?.role !== 'VIEWER'} />}
        </>
      )}
    </div>
  );
}

function KpiDetail({ kpi, canRegister }: { kpi: KpiDto; canRegister: boolean }) {
  const queryClient = useQueryClient();
  const chart = useChartTheme();
  const line = kpi.area ? areaColorFor(kpi.area.color, chart.theme) : chart.accent;
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const addPoint = useMutation({
    mutationFn: () =>
      api.post(`/kpis/${kpi.id}/datapoints`, {
        date,
        value: Number(value.replace(',', '.')),
        sourceDetail: 'Manuell registrering i Nova',
      }),
    onSuccess: () => {
      setAdding(false);
      setValue('');
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const series =
    kpi.datapoints?.map((d) => {
      const parsed = new Date(d.date);
      return {
        label: `${MONTHS_SHORT[parsed.getMonth()]} ${String(parsed.getFullYear()).slice(2)}`,
        value: d.value,
      };
    }) ?? [];

  const attainment = kpi.attainment;
  const lowerIsBetter = kpi.unit === 'timer' || kpi.unit === 'dager';

  return (
    <GlassPanel>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-accent" aria-hidden />
            <h2 className="font-display text-ink text-base">{kpi.name}</h2>
          </div>
          {kpi.description && (
            <p className="text-ink-soft mt-1.5 max-w-2xl text-sm leading-relaxed">
              {kpi.description}
            </p>
          )}
          <div className="text-ink-soft mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="border-hairline bg-raised/50 rounded-full border px-2 py-0.5">
              Kilde: {KPI_SOURCE_LABELS[kpi.source]}
            </span>
            {kpi.area && (
              <span
                className="rounded-full border px-2 py-0.5"
                style={{
                  borderColor: `${kpi.area.color}55`,
                  color: areaColorFor(kpi.area.color, chart.theme),
                }}
              >
                {kpi.area.name}
              </span>
            )}
            {attainment !== null && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5',
                  attainment >= 1
                    ? 'bg-success/12 text-success'
                    : attainment >= 0.8
                      ? 'bg-warning/12 text-warning'
                      : 'bg-danger/12 text-danger',
                )}
              >
                {formatPercent(attainment * 100)} av mål
              </span>
            )}
          </div>
        </div>

        {canRegister && (
          <Button
            size="sm"
            icon={<Plus size={13} aria-hidden />}
            onClick={() => setAdding((v) => !v)}
          >
            Registrer måling
          </Button>
        )}
      </div>

      {adding && (
        <div className="border-hairline bg-base/30 mt-4 flex flex-wrap items-end gap-2 rounded-lg border p-3">
          <div>
            <label htmlFor="kpi-date" className="label-micro mb-1 block">
              Dato
            </label>
            <input
              id="kpi-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
            />
          </div>
          <div>
            <label htmlFor="kpi-value" className="label-micro mb-1 block">
              Verdi ({kpi.unit})
            </label>
            <input
              id="kpi-value"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
              className="border-hairline bg-base/50 text-ink h-9 w-28 rounded-lg border px-2.5 text-sm"
            />
          </div>
          <Button
            size="sm"
            variant="primary"
            disabled={!value.trim() || Number.isNaN(Number(value.replace(',', '.')))}
            loading={addPoint.isPending}
            onClick={() => addPoint.mutate()}
          >
            Lagre
          </Button>
          <Button size="sm" onClick={() => setAdding(false)}>
            Avbryt
          </Button>
          {addPoint.isError && (
            <span className="text-danger text-xs">{(addPoint.error as Error).message}</span>
          )}
        </div>
      )}

      <div className="mt-5 h-64 w-full">
        {series.length < 2 ? (
          <EmptyState title="For få målinger til å tegne en graf ennå." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
              <CartesianGrid stroke={chart.grid} vertical={false} />
              <XAxis
                dataKey="label"
                stroke={chart.axis}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={chart.axis}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={56}
              />
              <Tooltip
                cursor={{ stroke: chart.cursor }}
                contentStyle={{
                  background: chart.tooltipBackground,
                  border: `1px solid ${chart.tooltipBorder}`,
                  borderRadius: 12,
                  fontSize: 12,
                  color: chart.tooltipText,
                }}
                formatter={(v: number) => [`${formatNumber(v, 1)} ${kpi.unit}`, kpi.name]}
              />
              <ReferenceLine
                y={kpi.targetValue}
                stroke={chart.reference}
                strokeDasharray="4 4"
                label={{
                  value: `Mål ${formatNumber(kpi.targetValue, 1)}`,
                  fill: chart.axis,
                  fontSize: 11,
                  position: lowerIsBetter ? 'insideBottomRight' : 'insideTopRight',
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={line}
                strokeWidth={2}
                dot={{ r: 2.5, strokeWidth: 0, fill: line }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassPanel>
  );
}
